import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { fundsRequestsApi, shopGroupsApi, shopsApi, usersApi } from 'api';
import { ProcessedByInfo } from 'components';
import { FilterDefinitionType, FundRequestTab, QueryKey } from 'enums';
import { FundsRequests } from 'features/funds-request';
import { useMeta, usePartialQuery, useShopsQuery, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { FilterDefinition, FundsRequest, FundsRequestFilters } from 'types';

export const ArchiveFundsRequests: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.funds_request.funds_requests',
  });

  const { data: users = [] } = useQuery(
    QueryKey.Users,
    usersApi.getAllWithPlatform,
  );

  const { role } = useUser();
  const { isShopGroupEnabled } = useMeta();

  const { data: shops = [] } = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsRole(role),
  );

  const { data: shopGroups = [] } = useQuery(
    QueryKey.ShopGroups,
    shopGroupsApi.getAllAsRole(role),
  );

  const queryResult = usePartialQuery(
    QueryKey.FundsRequests,
    fundsRequestsApi.getAllPaginatedByRole(role, FundRequestTab.Archived),
  );

  const additionalColumns = [
    {
      header: t('fields.processor'),
      valueGetter: (item: FundsRequest) => (
        <ProcessedByInfo
          processor={item.processor}
          processedAt={item.processedAt}
        />
      ),
    },
  ];

  const additionalFilters: FilterDefinition<FundsRequestFilters>[] = useMemo(
    () => [
      {
        label: t('fields.wallet'),
        name: 'wallet',
        type: FilterDefinitionType.Text,
      },
      {
        label: t('fields.hash'),
        name: 'hash',
        type: FilterDefinitionType.Text,
      },
    ],
    [t],
  );

  return (
    <FundsRequests
      archived
      additionalColumns={additionalColumns}
      additionalFilters={additionalFilters}
      queryResult={queryResult}
      hideTitle
      users={users}
      shops={shops}
      shopGroups={isShopGroupEnabled ? shopGroups : undefined}
    />
  );
};
